/**
 * StemiSetCard — surfaces when the cascade engine flags 'stemi'
 * (chest-pain or cardiac chief complaint with cardiac primary
 * impression, OR an explicit 12-lead-acquired finding).
 *
 * Captures first-12-lead time, interpretation, and copy-to-destination
 * intent. Triggers PCI Center Alert.
 */

import type { EPCR } from '@/types';
import { SetCard, LabeledRow, ChipSelect } from './SetCard';
import { FanOutChips } from '@/components/FanOutChips';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

type StemiFindings = {
  first_12_lead_time?: string;
  interpretation?: 'STEMI' | 'NSTEMI' | 'inconclusive' | 'normal';
  notes?: string;
  copy_sent_to_destination?: boolean;
};

export function StemiSetCard({ epcr, onUpdate }: Props) {
  const f = ((epcr.set_findings ?? {}).stemi ?? {}) as StemiFindings;
  const update = (patch: Partial<StemiFindings>) => {
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        stemi: { ...f, ...patch },
      },
    });
  };
  const locked = epcr.locked;

  return (
    <SetCard
      title="STEMI / Cardiac 12-Lead Set"
      triggeredBy="Chief complaint or primary impression suggests cardiac event. RI Protocol 4.x requires first-12-lead time + interpretation + PCI center pre-alert when STEMI."
      feeds={['nemsis_xml', 'riemsis_api', 'pci_alert', 'toc_handoff', 'narrative_engine']}
      accent="rose"
    >
      <LabeledRow label="First 12-Lead Time" hint="Door-to-EKG benchmark (≤10 min from patient contact).">
        <input
          type="datetime-local"
          disabled={locked}
          value={f.first_12_lead_time ?? ''}
          onChange={(e) => update({ first_12_lead_time: e.target.value })}
          className="rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
        />
        <FanOutChips capture="stemi.first_12_lead" />
      </LabeledRow>

      <LabeledRow label="12-Lead Interpretation">
        <ChipSelect
          options={['STEMI', 'NSTEMI', 'inconclusive', 'normal']}
          value={f.interpretation ?? null}
          disabled={locked}
          onChange={(v) => update({ interpretation: v as StemiFindings['interpretation'] })}
        />
      </LabeledRow>

      <LabeledRow label="Findings / Leads" hint="e.g. ST-elevation in II, III, aVF — inferior STEMI">
        <textarea
          disabled={locked}
          value={f.notes ?? ''}
          onChange={(e) => update({ notes: e.target.value })}
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          rows={2}
        />
      </LabeledRow>

      <LabeledRow label="Copy transmitted to destination?">
        <ChipSelect
          options={['yes', 'no', 'pending']}
          value={f.copy_sent_to_destination === true ? 'yes' : f.copy_sent_to_destination === false ? 'no' : null}
          disabled={locked}
          onChange={(v) => update({ copy_sent_to_destination: v === 'yes' ? true : v === 'no' ? false : undefined })}
        />
      </LabeledRow>
    </SetCard>
  );
}
